import { useEffect, useMemo, useState } from 'react';

import { TEditorTheme, TResolvedTheme } from './types';

/**
 * Resolves the editor theme setting to an actual 'dark' or 'light' value.
 * When theme is 'system', listens for OS prefers-color-scheme changes.
 */
const useResolvedTheme = (theme: TEditorTheme): TResolvedTheme => {
  const [systemPreference, setSystemPreference] = useState<TResolvedTheme>(() => {
    if (typeof window === 'undefined') return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    if (theme !== 'system') return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches ? 'dark' : 'light');
    };

    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [theme]);

  if (theme === 'system') return systemPreference;
  return theme;
};

export default useResolvedTheme;

/** Pure helper for non-hook contexts (e.g. tests) */
export const resolveTheme = (theme: TEditorTheme): TResolvedTheme => {
  if (theme === 'system') {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
};

/**
 * Color tokens for dark and light themes.
 * Covers all DevSandbox UI surfaces: shell, editor chrome, panels, popovers.
 */
interface IThemeColors {
  // Shell (nav, footer, main bg)
  shellBg: string;
  shellBorder: string;

  // Editor chrome (tab bar, editor container bg, dividers)
  editorBg: string;
  editorChromeBg: string;
  editorBorder: string;
  editorDivider: string;

  // Text colors
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textMuted: string;

  // Interactive elements
  hoverBg: string;
  activeBg: string;
  selectedBg: string;

  // Borders for controls
  controlBorder: string;
  controlBorderHover: string;
  controlBg: string;

  // Popover/overlay
  popoverBg: string;
  popoverBorder: string;
  popoverShadow: string;

  // Console panel
  consoleBg: string;
  consoleBorder: string;

  // Misc
  sectionLabel: string;
}

const darkColors: IThemeColors = {
  shellBg: 'gray.900',
  shellBorder: 'whiteAlpha.200',

  editorBg: '#282C34',
  editorChromeBg: '#1a1a24',
  editorBorder: 'whiteAlpha.200',
  editorDivider: 'whiteAlpha.100',

  textPrimary: 'whiteAlpha.900',
  textSecondary: 'whiteAlpha.800',
  textTertiary: 'whiteAlpha.600',
  textMuted: 'whiteAlpha.500',

  hoverBg: 'whiteAlpha.200',
  activeBg: 'whiteAlpha.300',
  selectedBg: 'whiteAlpha.200',

  controlBorder: 'whiteAlpha.300',
  controlBorderHover: 'whiteAlpha.400',
  controlBg: 'whiteAlpha.100',

  popoverBg: 'gray.800',
  popoverBorder: 'whiteAlpha.100',
  popoverShadow: '0 8px 32px rgba(0,0,0,0.5)',

  consoleBg: 'gray.900',
  consoleBorder: 'whiteAlpha.200',

  sectionLabel: 'whiteAlpha.600',
};

const lightColors: IThemeColors = {
  shellBg: 'white',
  shellBorder: 'gray.200',

  editorBg: '#ffffff',
  editorChromeBg: '#f7f8fa',
  editorBorder: 'gray.200',
  editorDivider: 'gray.200',

  textPrimary: 'gray.800',
  textSecondary: 'gray.700',
  textTertiary: 'gray.500',
  textMuted: 'gray.400',

  hoverBg: 'gray.100',
  activeBg: 'gray.200',
  selectedBg: 'gray.100',

  controlBorder: 'gray.300',
  controlBorderHover: 'gray.400',
  controlBg: 'gray.50',

  popoverBg: 'white',
  popoverBorder: 'gray.200',
  popoverShadow: '0 8px 32px rgba(0,0,0,0.12)',

  consoleBg: 'white',
  consoleBorder: 'gray.200',

  sectionLabel: 'gray.500',
};

export const getThemeColors = (resolved: TResolvedTheme): IThemeColors => {
  return resolved === 'light' ? lightColors : darkColors;
};

export const useThemeColors = (theme: TEditorTheme) => {
  const resolved = useResolvedTheme(theme);
  const colors = useMemo(() => getThemeColors(resolved), [resolved]);
  return { resolved, colors };
};

export type { IThemeColors };
